import { useState } from 'react';

import { CalendarClock, MessageSquare, Phone, Search, X } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useToast } from '@/hooks/use-toast';
import { useCancelScheduledMessage, useScheduledMessages } from '@/hooks/useScheduledMessages';

import {
  SCHEDULED_MESSAGE_CHANNEL_LABELS,
  SCHEDULED_MESSAGE_CHANNELS,
  SCHEDULED_MESSAGE_STATUS_LABELS,
  SCHEDULED_MESSAGE_STATUSES,
} from '@repo/shared-types';

import { ScheduledWorkflowRunsList } from './ScheduledWorkflowRunsList';

import type {
  ScheduledMessageChannel,
  ScheduledMessageStatus,
  ScheduledMessageWithRelations,
} from '@repo/shared-types';

const STATUS_TONE: Record<ScheduledMessageStatus, string> = {
  pending: 'bg-blue-500/10 text-blue-500',
  sending: 'bg-amber-500/10 text-amber-500',
  sent: 'bg-emerald-500/10 text-emerald-500',
  failed: 'bg-red-500/10 text-red-500',
  cancelled: 'bg-gray-500/10 text-gray-400',
};

const CHANNEL_ICON: Record<ScheduledMessageChannel, React.ReactNode> = {
  WHATSAPP: <MessageSquare className="h-3.5 w-3.5" />,
  SMS: <MessageSquare className="h-3.5 w-3.5" />,
  CALL: <Phone className="h-3.5 w-3.5" />,
};

const formatWhen = (iso: string) => {
  const date = new Date(iso);
  return date.toLocaleString('en-GB', {
    weekday: 'short',
    day: 'numeric',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  });
};

/** "in 3h 20m" / "2h ago" — the relative read is what matters at a glance. */
const relativeTo = (iso: string): string => {
  const diffMs = new Date(iso).getTime() - Date.now();
  const past = diffMs < 0;
  const minutes = Math.round(Math.abs(diffMs) / 60000);
  if (minutes < 1) return 'now';
  const hours = Math.floor(minutes / 60);
  const days = Math.floor(hours / 24);
  const text =
    days > 0 ? `${days}d ${hours % 24}h` : hours > 0 ? `${hours}h ${minutes % 60}m` : `${minutes}m`;
  return past ? `${text} ago` : `in ${text}`;
};

const ScheduledRow = ({ row }: { row: ScheduledMessageWithRelations }) => {
  const { toast } = useToast();
  const cancel = useCancelScheduledMessage();
  const [expanded, setExpanded] = useState(false);

  const preview = row.rendered_body || row.body || '';
  const isLong = preview.length > 180;
  const canCancel = row.status === 'pending';

  const handleCancel = async () => {
    try {
      const { cancelled } = await cancel.mutateAsync(row.id);
      toast({
        title: cancelled ? 'Scheduled send cancelled' : 'Already sent or cancelled',
        variant: cancelled ? undefined : 'destructive',
      });
    } catch {
      toast({ title: 'Failed to cancel', variant: 'destructive' });
    }
  };

  return (
    <div className="rounded-lg border p-4 dark:border-gray-700">
      <div className="flex items-start justify-between gap-4">
        <div className="min-w-0 flex-1 space-y-2">
          <div className="flex flex-wrap items-center gap-2">
            <span className="bg-muted inline-flex items-center gap-1.5 rounded-full px-2 py-0.5 text-[11px] font-medium">
              {CHANNEL_ICON[row.channel]}
              {SCHEDULED_MESSAGE_CHANNEL_LABELS[row.channel]}
            </span>
            <span
              className={`rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase ${STATUS_TONE[row.status]}`}
            >
              {SCHEDULED_MESSAGE_STATUS_LABELS[row.status]}
            </span>
            <span className="text-muted-foreground inline-flex items-center gap-1 text-xs">
              <CalendarClock className="h-3.5 w-3.5" />
              {formatWhen(row.scheduled_for)}
              <span className="text-muted-foreground/70">· {relativeTo(row.scheduled_for)}</span>
            </span>
          </div>

          <div className="text-sm font-medium">
            {row.contact?.name ? `${row.contact.name} · ` : ''}
            <span className="font-mono text-xs">{row.to_address}</span>
          </div>

          {row.channel === 'CALL' ? (
            <p className="text-muted-foreground text-xs">
              Outbound call{row.configuration?.name ? ` using ${row.configuration.name}` : ''}
            </p>
          ) : (
            <div className="bg-muted/40 rounded-md p-3">
              {row.template_name && (
                <p className="text-muted-foreground mb-1.5 font-mono text-[10px]">
                  {row.template_name}
                  {row.template_language ? ` · ${row.template_language}` : ''}
                </p>
              )}
              <p className="text-sm leading-relaxed whitespace-pre-line">
                {expanded || !isLong ? preview : `${preview.slice(0, 180)}...`}
              </p>
              {isLong && (
                <button
                  type="button"
                  onClick={() => setExpanded((v) => !v)}
                  className="text-muted-foreground hover:text-foreground mt-1.5 text-[11px] underline"
                >
                  {expanded ? 'Show less' : 'Show full message'}
                </button>
              )}
            </div>
          )}

          {row.booking && (
            <p className="text-muted-foreground text-[11px]">
              For booking &ldquo;{row.booking.title}&rdquo; ({row.booking.status}) on{' '}
              {formatWhen(row.booking.date)}
            </p>
          )}
          {row.error && <p className="text-[11px] text-red-500">{row.error}</p>}
        </div>

        {canCancel && (
          <Button
            size="sm"
            variant="ghost"
            className="text-destructive hover:text-destructive shrink-0 gap-1"
            onClick={handleCancel}
            disabled={cancel.isPending}
          >
            <X className="h-3.5 w-3.5" />
            Cancel
          </Button>
        )}
      </div>
    </div>
  );
};

export default function ScheduledMessagesPage() {
  const [channel, setChannel] = useState<string>('all');
  const [status, setStatus] = useState<string>('pending');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);

  const { data, isLoading } = useScheduledMessages({
    page,
    limit: 25,
    ...(channel !== 'all' ? { channel: channel as ScheduledMessageChannel } : {}),
    ...(status !== 'all' ? { status: status as ScheduledMessageStatus } : {}),
    ...(search ? { search } : {}),
  });

  const rows = data?.data ?? [];

  return (
    <div className="space-y-4 p-6">
      <div>
        <h1 className="text-xl font-semibold">Scheduled Sends</h1>
        <p className="text-muted-foreground text-sm">
          Messages, calls and workflow runs queued for later. Message text is exactly what will be
          sent — it was resolved when the workflow ran. Workflow runs decide what to send when they
          run.
        </p>
      </div>

      <Tabs defaultValue="messages">
        <TabsList>
          <TabsTrigger value="messages">Messages & calls</TabsTrigger>
          <TabsTrigger value="runs">Workflow runs</TabsTrigger>
        </TabsList>

        <TabsContent value="messages">
          <div className="space-y-4">
            <div className="flex flex-wrap items-center gap-2">
              <div className="relative">
                <Search className="text-muted-foreground pointer-events-none absolute top-1/2 left-2 h-3.5 w-3.5 -translate-y-1/2" />
                <Input
                  value={search}
                  onChange={(e) => {
                    setSearch(e.target.value);
                    setPage(1);
                  }}
                  placeholder="Search number or message..."
                  className="h-8 w-64 pl-7 text-sm"
                />
              </div>

              <Select
                value={channel}
                onValueChange={(v) => {
                  setChannel(v);
                  setPage(1);
                }}
              >
                <SelectTrigger className="h-8 w-36 text-sm">
                  <SelectValue placeholder="Channel" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All channels</SelectItem>
                  {SCHEDULED_MESSAGE_CHANNELS.map((c) => (
                    <SelectItem key={c} value={c}>
                      {SCHEDULED_MESSAGE_CHANNEL_LABELS[c]}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>

              <Select
                value={status}
                onValueChange={(v) => {
                  setStatus(v);
                  setPage(1);
                }}
              >
                <SelectTrigger className="h-8 w-36 text-sm">
                  <SelectValue placeholder="Status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All statuses</SelectItem>
                  {SCHEDULED_MESSAGE_STATUSES.map((s) => (
                    <SelectItem key={s} value={s}>
                      {SCHEDULED_MESSAGE_STATUS_LABELS[s]}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {isLoading ? (
              <p className="text-muted-foreground text-sm">Loading...</p>
            ) : rows.length === 0 ? (
              <div className="rounded-lg border border-dashed p-10 text-center">
                <CalendarClock className="text-muted-foreground mx-auto mb-2 h-6 w-6" />
                <p className="text-sm font-medium">Nothing scheduled</p>
                <p className="text-muted-foreground text-xs">
                  A workflow queues sends here when its WhatsApp/SMS or call node has a send time.
                </p>
              </div>
            ) : (
              <div className="space-y-2">
                {rows.map((row) => (
                  <ScheduledRow key={row.id} row={row} />
                ))}
              </div>
            )}

            {data && data.pagination.totalPages > 1 && (
              <div className="flex items-center justify-between">
                <p className="text-muted-foreground text-xs">
                  Page {data.pagination.page} of {data.pagination.totalPages} ·{' '}
                  {data.pagination.total} total
                </p>
                <div className="flex gap-2">
                  <Button
                    size="sm"
                    variant="outline"
                    disabled={page <= 1}
                    onClick={() => setPage((p) => p - 1)}
                  >
                    Previous
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    disabled={page >= data.pagination.totalPages}
                    onClick={() => setPage((p) => p + 1)}
                  >
                    Next
                  </Button>
                </div>
              </div>
            )}
          </div>
        </TabsContent>

        <TabsContent value="runs">
          <ScheduledWorkflowRunsList />
        </TabsContent>
      </Tabs>
    </div>
  );
}
